import { useState, useEffect } from 'react';
import { authService } from '../services/api';

/**
 * Custom hook for authentication management
 * Handles login, logout, and authentication state
 */
export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    setIsAuthenticated(authService.isAuthenticated());
    setLoading(false);
  }, []);

  const handleLogin = async (username, password) => {
    try {
      await authService.login(username, password);
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
      throw error; // Re-throw to let LoginPage handle the error
    }
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
  };

  // For login paths that store the token themselves (Google Sign-In posts
  // directly to the backend and writes localStorage itself, unlike
  // handleLogin's username/password flow) — this just syncs the React
  // state so protected routes actually unlock afterward.
  const markAuthenticated = () => setIsAuthenticated(true);

  return {
    isAuthenticated,
    loading,
    handleLogin,
    handleLogout,
    markAuthenticated
  };
}

/**
 * Custom hook for language management
 * Handles language switching and persistence
 */
export function useLanguage() {
  const [language, setLanguage] = useState('ar'); // Default to Arabic

  useEffect(() => {
    // Load saved language preference
    const savedLanguage = localStorage.getItem('language') || 'ar';
    setLanguage(savedLanguage);
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'ar' ? 'en' : 'ar';
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  return {
    language,
    toggleLanguage
  };
}

/**
 * Custom hook for filter management
 * Handles filter state and active filter count
 */
export function useFilters() {
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);

  const toggleFilter = () => {
    setFilterOpen(prev => !prev);
  };

  const handleFilterToggle = (isOpen) => {
    setFilterOpen(isOpen);
  };

  const handleActiveFiltersChange = (count) => {
    setActiveFilters(count);
  };

  return {
    filterOpen,
    activeFilters,
    toggleFilter,
    handleFilterToggle,
    handleActiveFiltersChange
  };
}

/**
 * Custom hook for search management
 * Handles search query state
 */
export function useSearch() {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchChange = (query) => {
    setSearchQuery(query);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return {
    searchQuery,
    handleSearchChange,
    clearSearch
  };
}

/**
 * Custom hook for the main content-area view mode: the Leaflet map, the
 * embedded PACI/Kuwait Finder map, or the card-based list view. Replaces the
 * old boolean-only PACI toggle so a third ('list') state fits without
 * threading a second flag through Header/MapDetailsPage.
 */
export function useViewMode() {
  const [viewMode, setViewMode] = useState('map'); // 'map' | 'paci' | 'list'

  return {
    viewMode,
    setViewMode
  };
}
